import React, { useEffect, useRef, useState } from "react";
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Eye,
  X,
  Upload,
  ChevronRight,
  CheckCircle2,
} from "lucide-react";
import { useSelector } from "react-redux";
import { message as antMessage } from "antd";
import CallApi from "../../Reusable/CallApi/CallApi";

const COLORS = {
  primary: "#3DCFB6",
  primaryHover: "#33B9A2",
  border: "#D9DEE3",
  headerBg: "#FAFAFA",
  headerText: "#1F243B",
  rowText: "#1F243B",
  rowSubText: "#636980",
  pageBg: "#FFFFFF",
  danger: "#E85D5D",
};

const emptyForm = {
  name: "",
  description: "",
  groupKey: false,
  // Only used when Group Key is ticked - saved as enter_groupkey /
  // maximum_limit on tymeplushrlmsgroup.
  enterGroupKey: "",
  maxLimit: "",
};

// Seats left in a group with a Maximum Limit (null = no limit).
const seatsLeft = (group) =>
  group?.key && group?.maxLimit
    ? Math.max(group.maxLimit - (group.members || []).length, 0)
    : null;

/* -------------------------------------------------------
   MODAL
------------------------------------------------------- */

function Modal({ open, onClose, children, maxWidth = 560 }) {
  if (!open) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 50,
        padding: 16,
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        style={{
          background: COLORS.pageBg,
          borderRadius: 10,
          width: "100%",
          maxWidth,
          boxShadow:
            "0 6px 16px rgba(0,0,0,0.08), 0 3px 6px rgba(0,0,0,0.12)",
          overflow: "hidden",
        }}
      >
        {children}
      </div>
    </div>
  );
}

function ModalHeader({ title, onClose }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "18px 24px",
        borderBottom: `1px solid ${COLORS.border}`,
      }}
    >
      <h3
        style={{
          margin: 0,
          fontSize: 18,
          fontWeight: 500,
          color: "#1F243B",
        }}
      >
        {title}
      </h3>

      <button
        onClick={onClose}
        style={{
          border: "none",
          background: "transparent",
          cursor: "pointer",
          padding: 4,
          display: "flex",
        }}
      >
        <X size={18} color="#6B7280" />
      </button>
    </div>
  );
}

function FormLabel({ children }) {
  return (
    <label
      style={{
        display: "block",
        fontSize: 15,
        fontWeight: 600,
        color: COLORS.rowText,
        marginBottom: 8,
      }}
    >
      {children}
    </label>
  );
}

function SuccessModal({ open, onClose, title, message }) {
  return (
    <Modal open={open} onClose={onClose} maxWidth={320}>
      <div
        style={{
          padding: "32px 24px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 12,
          textAlign: "center",
        }}
      >
        <CheckCircle2 size={40} color={COLORS.primary} />

        <div
          style={{
            fontSize: 16,
            fontWeight: 700,
            color: "#1F243B",
          }}
        >
          {title}
        </div>

        <div
          style={{
            fontSize: 13,
            color: COLORS.rowSubText,
          }}
        >
          {message}
        </div>

        <button
          onClick={onClose}
          style={{
            border: "none",
            background: "#C2FAE5",
            color: "#1F243B",
            borderRadius: 8,
            padding: "10px 20px",
            fontSize: 14,
            fontWeight: 400,
            cursor: "pointer",
            marginTop: 8,
            width: "100%",
          }}
        >
          Done
        </button>
      </div>
    </Modal>
  );
}

/* -------------------------------------------------------
   STYLES
------------------------------------------------------- */

const fieldStyle = {
  width: "100%",
  boxSizing: "border-box",
  border: `1px solid ${COLORS.border}`,
  borderRadius: 6,
  padding: "10px 12px",
  fontSize: 15,
  fontWeight: 400,
  color: COLORS.rowText,
  outline: "none",
  background: "#FFFFFF",
  fontFamily: "inherit",
};

const ghostBtn = {
  border: `1px solid ${COLORS.border}`,
  background: "#FFFFFF",
  color: COLORS.rowText,
  borderRadius: 8,
  padding: "10px 20px",
  fontSize: 14,
  fontWeight: 400,
  cursor: "pointer",
};

const primaryBtn = {
  border: "none",
  background: "#C2FAE5",
  color: "#1F243B",
  borderRadius: 8,
  padding: "10px 20px",
  fontSize: 14,
  fontWeight: 400,
  cursor: "pointer",
};

const iconBtn = {
  border: `1px solid ${COLORS.border}`,
  background: "#FFFFFF",
  borderRadius: 6,
  padding: 6,
  cursor: "pointer",
  display: "flex",
};

/* -------------------------------------------------------
   COMPONENT
------------------------------------------------------- */

export default function ReportGroupPeople() {
  const clientId = useSelector(
    (state) => state.authTracking.userInfo?.clientId
  );

  const [groups, setGroups] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [view, setView] = useState("list");
  const [selectedGroupId, setSelectedGroupId] = useState(null);

  const [search, setSearch] = useState("");

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("create");
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [formError, setFormError] = useState("");
  const [formSubmitting, setFormSubmitting] = useState(false);
  // Name of the group just created - shows the "Created" popup.
  const [createdGroupName, setCreatedGroupName] = useState(null);

  const [focusField, setFocusField] = useState(null);

  const [form, setForm] = useState(emptyForm);

  const [addStep, setAddStep] = useState(null);

  const [availableEmployees, setAvailableEmployees] = useState([]);
  const [availableEmployeesLoading, setAvailableEmployeesLoading] = useState(false);
  const [selectedEmpIds, setSelectedEmpIds] = useState(new Set());
  const [enrolling, setEnrolling] = useState(false);

  const [lastAddedCount, setLastAddedCount] = useState(0);

  const [detailTab, setDetailTab] = useState("People");

  const [memberSearch, setMemberSearch] = useState("");
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState("");

  const [courseSearch, setCourseSearch] = useState("");
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [coursesError, setCoursesError] = useState("");

  const [courseStep, setCourseStep] = useState(null);

  const [availableCourses, setAvailableCourses] = useState([]);
  const [availableCoursesLoading, setAvailableCoursesLoading] = useState(false);
  const [selectedCourseIds, setSelectedCourseIds] = useState(new Set());
  const [addingCourses, setAddingCourses] = useState(false);

  const [lastAddedCourseCount, setLastAddedCourseCount] = useState(0);

  const [filesLoading, setFilesLoading] = useState(false);
  const [filesListError, setFilesListError] = useState("");
  const [fileFormOpen, setFileFormOpen] = useState(false);
  const [fileUploading, setFileUploading] = useState(false);
  const [fileError, setFileError] = useState("");

  const [stagedFiles, setStagedFiles] = useState([]);

  const [isDraggingFile, setIsDraggingFile] = useState(false);

  const [viewingFile, setViewingFile] = useState(null);

  const fileInputRef = useRef(null);

  /* -------------------------------------------------------
     GET GROUP API
  ------------------------------------------------------- */

  useEffect(() => {
    if (!clientId) return;
    getGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const getGroups = async () => {
    try {
      setLoading(true);
      setError("");

      const result = await CallApi({
        endPoint: `/group/getgroup/${clientId}`,
        method: "get",
      });

      if (result?.response && Array.isArray(result.response)) {
        const formattedGroups = result.response.map((group) => ({
          id: group.group_id,
          name: group.group_name || "",
          key: group.group_key ?? false,
          enterKey: group.enter_groupkey || "",
          maxLimit: group.maximum_limit ? Number(group.maximum_limit) : null,
          description: group.description || "—",

          // Populated on demand when a group is opened / a tab is viewed
          // (see loadGroupPeople/loadGroupCourses/loadGroupFiles) rather
          // than eagerly for every row in the list.
          members: [],
          courses: [],
          files: [],
          membersLoaded: false,
          coursesLoaded: false,
          filesLoaded: false,
        }));

        setGroups(formattedGroups);
      } else {
        setGroups([]);
      }
    } catch (err) {
      console.error("GET GROUP API ERROR:", err);

      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load groups"
      );
    } finally {
      setLoading(false);
    }
  };

  /* -------------------------------------------------------
     SELECTED GROUP
  ------------------------------------------------------- */

  const selectedGroup = groups.find(
    (group) => group.id === selectedGroupId
  );

  /* -------------------------------------------------------
     SEARCH
  ------------------------------------------------------- */

  const filteredGroups = groups.filter((group) => {
    const groupName = String(group.name || "").toLowerCase();

    const description = String(group.description || "").toLowerCase();

    const searchValue = search.toLowerCase();

    return (
      groupName.includes(searchValue) ||
      description.includes(searchValue)
    );
  });

  /* -------------------------------------------------------
     GROUP FORM
  ------------------------------------------------------- */

  const openCreateForm = () => {
    setFormMode("create");
    setEditingGroupId(null);
    setForm(emptyForm);
    setFormOpen(true);
  };

  const openEditForm = (group, e) => {
    if (e) {
      e.stopPropagation();
    }

    setFormMode("edit");
    setEditingGroupId(group.id);

    setForm({
      name: group.name,
      description: group.description,
      groupKey: Boolean(group.key),
      enterGroupKey: group.enterKey || "",
      maxLimit: group.maxLimit ? String(group.maxLimit) : "",
    });

    setFormOpen(true);
  };

  const handleFormSubmit = async () => {
    if (!form.name.trim() || !form.description.trim()) {
      setFormError("Group name and description are required.");
      return;
    }

    const enterKey = form.enterGroupKey.trim();
    const limitText = String(form.maxLimit).trim();
    const limit = limitText === "" ? null : Number(limitText);

    if (form.groupKey) {
      if (!enterKey) {
        setFormError("Enter a group key, or untick Group Key.");
        return;
      }
      if (!/^[A-Za-z0-9_-]{4,20}$/.test(enterKey)) {
        setFormError("Group key must be 4-20 letters, numbers, - or _ (no spaces).");
        return;
      }
      if (limit !== null && (!Number.isInteger(limit) || limit < 1)) {
        setFormError("Maximum limit must be a whole number of 1 or more.");
        return;
      }
      const currentMembers =
        formMode === "edit"
          ? (groups.find((group) => group.id === editingGroupId)?.members || []).length
          : 0;
      if (limit !== null && limit < currentMembers) {
        setFormError(`This group already has ${currentMembers} people - the maximum limit can't be lower.`);
        return;
      }
    }

    setFormSubmitting(true);
    setFormError("");

    const payload = {
      group_name: form.name.trim(),
      description: form.description.trim(),
      group_key: !!form.groupKey,
      // tymeplushrlmsgroup.enter_groupkey is NOT NULL - "" is the correct
      // "no key" value, not null. Unticking Group Key clears both fields.
      enter_groupkey: form.groupKey ? enterKey : "",
      maximum_limit: form.groupKey ? limit : null,
    };

    try {
      if (formMode === "edit") {
        const result = await CallApi({
          endPoint: `/group/updategroup/${clientId}/${editingGroupId}`,
          method: "patch",
          data: payload,
        });

        const updated = result?.response?.[0];

        setGroups((prev) =>
          prev.map((group) =>
            group.id === editingGroupId
              ? {
                  ...group,
                  name: updated?.group_name ?? payload.group_name,
                  description: updated?.description ?? payload.description,
                  key: updated?.group_key ?? payload.group_key,
                  enterKey: updated?.enter_groupkey ?? payload.enter_groupkey,
                  maxLimit: updated
                    ? updated.maximum_limit
                      ? Number(updated.maximum_limit)
                      : null
                    : payload.maximum_limit,
                }
              : group
          )
        );
      } else {
        const result = await CallApi({
          endPoint: `/group/addgroup/${clientId}`,
          method: "post",
          data: payload,
        });

        const created = result?.response?.[0];

        if (created) {
          setGroups((prev) => [
            ...prev,
            {
              id: created.group_id,
              name: created.group_name,
              key: created.group_key ?? false,
              enterKey: created.enter_groupkey || "",
              maxLimit: created.maximum_limit ? Number(created.maximum_limit) : null,
              description: created.description || "—",
              members: [],
              courses: [],
              files: [],
              membersLoaded: false,
              coursesLoaded: false,
              filesLoaded: false,
            },
          ]);
        }

        setCreatedGroupName(created?.group_name || payload.group_name);
      }

      setFormOpen(false);
    } catch (err) {
      console.error("Group save error:", err);
      // Backend validation errors (e.g. a duplicate group key) come back
      // as a plain string body, others as { message }.
      const data = err?.response?.data;
      setFormError(
        (typeof data === "string" ? data : data?.message) ||
          err?.message ||
          "Failed to save group"
      );
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleDeleteGroup = async (id, e) => {
    e.stopPropagation();

    try {
      await CallApi({
        endPoint: `/group/deletegroup/${clientId}/${id}`,
        method: "delete",
      });

      setGroups((prev) => prev.filter((group) => group.id !== id));

      if (selectedGroupId === id) {
        setSelectedGroupId(null);
        setView("list");
      }
    } catch (err) {
      console.error("Delete group error:", err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to delete group"
      );
    }
  };

  /* -------------------------------------------------------
     OPEN DETAIL
  ------------------------------------------------------- */

  const openRow = (id) => {
    setSelectedGroupId(id);

    setView("detail");

    setDetailTab("People");

    setMemberSearch("");

    loadGroupPeople(id);
  };

  const backToList = () => {
    setView("list");
    setSelectedGroupId(null);
  };

  const inputStyle = (field) => ({
    ...fieldStyle,
    borderColor:
      focusField === field
        ? COLORS.primary
        : COLORS.border,
    boxShadow:
      focusField === field
        ? `0 0 0 2px ${COLORS.primary}33`
        : "none",
  });

  /* -------------------------------------------------------
     PEOPLE
  ------------------------------------------------------- */

  const filteredMembers =
    selectedGroup?.members?.filter(
      (member) =>
        String(member.name || "")
          .toLowerCase()
          .includes(memberSearch.toLowerCase()) ||
        String(member.empId || "")
          .toLowerCase()
          .includes(memberSearch.toLowerCase())
    ) || [];

  const loadGroupPeople = async (groupId) => {
    setMembersLoading(true);
    setMembersError("");

    try {
      const result = await CallApi({
        endPoint: `/group/getpeople/${clientId}/${groupId}`,
        method: "get",
      });

      const members = (result?.response || []).map((person) => ({
        id: person.people_id,
        empId: person.people_id,
        name: `${person.first_name || ""} ${person.last_name || ""}`.trim() || "—",
        role: person.peopel_type || person.competency || "—",
        courses: "—",
      }));

      setGroups((prev) =>
        prev.map((group) =>
          group.id === groupId
            ? { ...group, members, membersLoaded: true }
            : group
        )
      );
    } catch (err) {
      console.error("Get group people error:", err);
      setMembersError(
        err?.response?.data?.message || err?.message || "Failed to load employees"
      );
    } finally {
      setMembersLoading(false);
    }
  };

  const openAddEmployees = async () => {
    if (!selectedGroup) return;

    if (addSeatsLeft === 0) {
      antMessage.warning(
        `This group is full (${selectedGroup.maxLimit} people). Raise its Maximum Limit to add more.`
      );
      return;
    }

    setSelectedEmpIds(new Set());
    setAvailableEmployees([]);
    setAddStep("choose");
    setAvailableEmployeesLoading(true);

    try {
      const result = await CallApi({
        endPoint: `/group/availablepeople/${clientId}/${selectedGroup.id}`,
        method: "get",
      });

      setAvailableEmployees(
        (result?.response || []).map((person) => ({
          id: person.people_id,
          name: `${person.first_name || ""} ${person.last_name || ""}`.trim() || "—",
          empId: person.people_id,
        }))
      );
    } catch (err) {
      console.error("Get available people error:", err);
    } finally {
      setAvailableEmployeesLoading(false);
    }
  };

  const closeAddFlow = () => {
    setAddStep(null);
    setSelectedEmpIds(new Set());
    setAvailableEmployees([]);
  };

  // Maximum Limit (Group Key) caps how many people can be picked here.
  const addSeatsLeft = seatsLeft(selectedGroup);

  const toggleEmp = (id) => {
    setSelectedEmpIds((prev) => {
      const next = new Set(prev);

      if (next.has(id)) {
        next.delete(id);
      } else {
        if (addSeatsLeft !== null && next.size >= addSeatsLeft) return prev;
        next.add(id);
      }

      return next;
    });
  };

  const toggleAllEmps = () => {
    setSelectedEmpIds((prev) => {
      const selectable =
        addSeatsLeft === null ? availableEmployees : availableEmployees.slice(0, addSeatsLeft);
      return prev.size > 0 && prev.size === selectable.length
        ? new Set()
        : new Set(selectable.map((employee) => employee.id));
    });
  };

  const handleEnrollFromList = async () => {
    if (!selectedGroup || selectedEmpIds.size === 0) {
      return;
    }

    setEnrolling(true);

    try {
      const ids = Array.from(selectedEmpIds);

      // The backend only accepts one people_id per call, so these run
      // sequentially rather than as a single batched request.
      for (const peopleId of ids) {
        // eslint-disable-next-line no-await-in-loop
        await CallApi({
          endPoint: `/group/addpeople/${clientId}/${selectedGroup.id}`,
          method: "post",
          data: { people_id: peopleId },
        });
      }

      setLastAddedCount(ids.length);
      await loadGroupPeople(selectedGroup.id);
      setAddStep("success");
    } catch (err) {
      console.error("Enroll employees error:", err);
      // e.g. "This group is full" from the backend's Maximum Limit check.
      const data = err?.response?.data;
      antMessage.error((typeof data === "string" ? data : data?.message) || "Failed to add people");
      await loadGroupPeople(selectedGroup.id);
    } finally {
      setEnrolling(false);
    }
  };

  const finishAddSuccess = () => {
    closeAddFlow();
  };

  const handleRemoveMember = async (peopleId) => {
    if (!selectedGroup) return;

    try {
      await CallApi({
        endPoint: `/group/deletepeople/${clientId}/${selectedGroup.id}/${peopleId}`,
        method: "delete",
      });

      setGroups((prev) =>
        prev.map((group) =>
          group.id === selectedGroup.id
            ? {
                ...group,
                members: (group.members || []).filter(
                  (member) => member.id !== peopleId
                ),
              }
            : group
        )
      );
    } catch (err) {
      console.error("Remove member error:", err);
    }
  };

  /* -------------------------------------------------------
     COURSE
     Note: group-course association uses the legacy
     tymeplushrlmscourse catalog table, not
     tymeplushrlmsallcourse (the table the Course module
     writes to) - the two were never reconciled on the
     backend, so courses created via Course won't show up
     here to add until that's addressed server-side.
  ------------------------------------------------------- */

  const filteredCourses =
    selectedGroup?.courses?.filter(
      (course) =>
        String(course.name || "")
          .toLowerCase()
          .includes(
            courseSearch.toLowerCase()
          ) ||
        String(course.code || "")
          .toLowerCase()
          .includes(
            courseSearch.toLowerCase()
          )
    ) || [];

  const loadGroupCourses = async (groupId) => {
    setCoursesLoading(true);
    setCoursesError("");

    try {
      const result = await CallApi({
        endPoint: `/group/getcourse/${clientId}/${groupId}`,
        method: "get",
      });

      const courses = (result?.response || []).map((course) => ({
        id: course.course_id,
        code: course.course_id,
        name: course.course_name,
        category: course.type || "—",
        price: "—",
      }));

      setGroups((prev) =>
        prev.map((group) =>
          group.id === groupId
            ? { ...group, courses, coursesLoaded: true }
            : group
        )
      );
    } catch (err) {
      console.error("Get group courses error:", err);
      setCoursesError(
        err?.response?.data?.message || err?.message || "Failed to load courses"
      );
    } finally {
      setCoursesLoading(false);
    }
  };

  const openCourseChoose = async () => {
    if (!selectedGroup) return;

    setSelectedCourseIds(new Set());
    setAvailableCourses([]);
    setCourseStep("choose");
    setAvailableCoursesLoading(true);

    try {
      const result = await CallApi({
        endPoint: `/group/availablecourse/${clientId}/${selectedGroup.id}`,
        method: "get",
      });

      setAvailableCourses(
        (result?.response || []).map((course) => ({
          id: course.course_id,
          name: course.course_name,
        }))
      );
    } catch (err) {
      console.error("Get available courses error:", err);
    } finally {
      setAvailableCoursesLoading(false);
    }
  };

  const closeCourseFlow = () => {
    setCourseStep(null);
    setSelectedCourseIds(new Set());
    setAvailableCourses([]);
  };

  const toggleCourse = (id) => {
    setSelectedCourseIds((prev) => {
      const next = new Set(prev);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  };

  const toggleAllCourses = () => {
    setSelectedCourseIds((prev) =>
      prev.size === availableCourses.length
        ? new Set()
        : new Set(availableCourses.map((course) => course.id))
    );
  };

  const handleAddCourses = async () => {
    if (!selectedGroup || selectedCourseIds.size === 0) {
      return;
    }

    setAddingCourses(true);

    try {
      const ids = Array.from(selectedCourseIds);

      await CallApi({
        endPoint: `/group/addcourse/${clientId}/${selectedGroup.id}`,
        method: "post",
        data: { course_ids: ids },
      });

      setLastAddedCourseCount(ids.length);
      await loadGroupCourses(selectedGroup.id);
      setCourseStep("success");
    } catch (err) {
      console.error("Add courses error:", err);
    } finally {
      setAddingCourses(false);
    }
  };

  const finishCourseSuccess = () => {
    closeCourseFlow();
  };

  const handleRemoveCourse = async (courseId) => {
    if (!selectedGroup) return;

    try {
      await CallApi({
        endPoint: `/group/deletecourse/${clientId}/${selectedGroup.id}/${courseId}`,
        method: "delete",
      });

      setGroups((prev) =>
        prev.map((group) =>
          group.id === selectedGroup.id
            ? {
                ...group,
                courses: (group.courses || []).filter(
                  (course) => course.id !== courseId
                ),
              }
            : group
        )
      );
    } catch (err) {
      console.error("Remove course error:", err);
    }
  };

  /* -------------------------------------------------------
     FILES
  ------------------------------------------------------- */

  const loadGroupFiles = async (groupId) => {
    setFilesLoading(true);
    setFilesListError("");

    try {
      const result = await CallApi({
        endPoint: `/group/getfiles/${clientId}/${groupId}`,
        method: "get",
      });

      const files = (result?.response || []).map((file) => ({
        id: file.id,
        name: file.file_name,
        url: file.file_url,
        type: file.file_type,
      }));

      setGroups((prev) =>
        prev.map((group) =>
          group.id === groupId
            ? { ...group, files, filesLoaded: true }
            : group
        )
      );
    } catch (err) {
      console.error("Get group files error:", err);
      setFilesListError(
        err?.response?.data?.message || err?.message || "Failed to load files"
      );
    } finally {
      setFilesLoading(false);
    }
  };

  const openAddFile = () => {
    setFileError("");
    setStagedFiles([]);
    setFileFormOpen(true);
  };

  const addStagedFiles = (fileList) => {
    setStagedFiles((prev) => [...prev, ...Array.from(fileList)]);
  };

  const handleFilePicked = (e) => {
    // Snapshot to a plain array synchronously - e.target.files is a live
    // FileList tied to the input, and clearing e.target.value right below
    // clears that same live list. addStagedFiles' setState updater runs
    // later, so passing the FileList itself meant Array.from(fileList) saw
    // an already-emptied list by the time it ran (files picked via
    // "clicking here" silently never got staged, while drag/drop worked
    // fine since handleFileDrop never mutates dataTransfer.files after).
    const files = Array.from(e.target.files || []);
    if (files.length) {
      addStagedFiles(files);
    }

    e.target.value = "";
  };

  const handleFileDrop = (e) => {
    e.preventDefault();

    setIsDraggingFile(false);

    if (
      e.dataTransfer.files &&
      e.dataTransfer.files.length
    ) {
      addStagedFiles(e.dataTransfer.files);
    }
  };

  const removeStagedFile = (index) => {
    setStagedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleFileFormSubmit = async () => {
    // Previously silently no-op'd here with the button just disabled and
    // no feedback - if file staging (drag/drop or the hidden <input>)
    // ever fails silently, there was no way to tell from the UI. Now it
    // always responds to a click.
    if (!selectedGroup) {
      setFileError("No group selected.");
      return;
    }
    if (stagedFiles.length === 0) {
      setFileError("Select or drop at least one file first.");
      return;
    }

    setFileUploading(true);
    setFileError("");

    try {
      // Reuses the Course module's presigned-upload endpoint (it's just an
      // S3 key-prefix choice server-side, not actually course-specific) -
      // there's no dedicated group-file upload endpoint.
      for (const file of stagedFiles) {
        // eslint-disable-next-line no-await-in-loop
        const uploadUrlResult = await CallApi({
          endPoint: `/course/getuploadurl/${clientId}`,
          method: "post",
          data: { file_name: file.name, content_type: file.type },
        });

        const { uploadUrl, fileUrl } = uploadUrlResult?.response || {};

        if (!uploadUrl || !fileUrl) {
          throw new Error("Could not get an upload URL");
        }

        // eslint-disable-next-line no-await-in-loop
        await fetch(uploadUrl, {
          method: "PUT",
          headers: { "Content-Type": file.type || "application/octet-stream" },
          body: file,
        });

        // eslint-disable-next-line no-await-in-loop
        await CallApi({
          endPoint: `/group/addfile/${clientId}/${selectedGroup.id}`,
          method: "post",
          data: {
            file_name: file.name,
            file_url: fileUrl,
            file_type: file.name.split(".").pop(),
          },
        });
      }

      await loadGroupFiles(selectedGroup.id);
      setFileFormOpen(false);
      setStagedFiles([]);
    } catch (err) {
      console.error("Upload file error:", err);
      setFileError(
        err?.response?.data?.message || err?.message || "Upload failed"
      );
    } finally {
      setFileUploading(false);
    }
  };

  const handleDeleteFile = async (id) => {
    if (!selectedGroup) return;

    try {
      await CallApi({
        endPoint: `/group/deletefile/${clientId}/${selectedGroup.id}/${id}`,
        method: "delete",
      });

      setGroups((prev) =>
        prev.map((group) =>
          group.id === selectedGroup.id
            ? {
                ...group,
                files: (group.files || []).filter((file) => file.id !== id),
              }
            : group
        )
      );
    } catch (err) {
      console.error("Delete file error:", err);
    }
  };

  /* -------------------------------------------------------
     UI
  ------------------------------------------------------- */

  return (
    <div
      style={{
        fontFamily: "'DM Sans', sans-serif",
        background: COLORS.pageBg,
        borderRadius: 12,
        padding: 0,
        // maxWidth: 1000,
        margin: "0 auto",
      }}
    >
 

      {view === "list" && (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              marginBottom: 4,
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            <div>
              <h2
                style={{
                  fontSize: 20,
                  fontWeight: 500,
                  color: "#1F243B",
                  margin: 0,
                }}
              >
                Group
              </h2>

              <div
                style={{
                  fontSize: 13,
                  color: COLORS.rowSubText,
                  marginTop: 4,
                }}
              >
                Total Group:{" "}
                <span
                  style={{
                    color: "#D97706",
                  }}
                >
                  {groups.length}
                </span>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
              }}
            >
              <div
                style={{
                  position: "relative",
                }}
              >
                <Search
                  size={15}
                  color="#9CA3AF"
                  style={{
                    position: "absolute",
                    left: 10,
                    top: "50%",
                    transform:
                      "translateY(-50%)",
                  }}
                />

                <input
                  value={search}
                  onChange={(e) =>
                    setSearch(e.target.value)
                  }
                  placeholder="Search"
                  style={{
                    ...fieldStyle,
                    width: 280,
                    borderRadius: 24,
                    padding:
                      "7px 12px 7px 32px",
                    fontSize: 14,
                  }}
                />
              </div>

              <button
                onClick={openCreateForm}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  ...primaryBtn,
                  padding: "10px 20px",
                }}
              >
                <Plus size={16} />
                Create Group
              </button>
            </div>
          </div>

          {/* TABLE */}

          <div
            style={{
              marginTop: 20,
              border: `1px solid ${COLORS.border}`,
              borderRadius: 8,
              overflow: "hidden",
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
              }}
            >
              <thead>
                <tr
                  style={{
                    background: COLORS.headerBg,
                  }}
                >
                  <th
                    style={{
                      padding: "16px",
                      fontSize: 14,
                      fontWeight: 600,
                      textAlign: "left",
                      color: COLORS.headerText,
                      width: 200,
                    }}
                  >
                    Name
                  </th>

                  <th
                    style={{
                      padding: "16px",
                      fontSize: 14,
                      fontWeight: 600,
                      textAlign: "left",
                      color: COLORS.headerText,
                    }}
                  >
                    Description
                  </th>

                  <th
                    style={{
                      padding: "16px",
                      fontSize: 14,
                      fontWeight: 600,
                      textAlign: "right",
                      color: COLORS.headerText,
                      width: 100,
                    }}
                  >
                    Action
                  </th>
                </tr>
              </thead>

              <tbody
                style={{
                  background: "#FFFFFF",
                }}
              >
                {loading ? (
                  <tr>
                    <td
                      colSpan={3}
                      style={{
                        padding: "40px 20px",
                        textAlign: "center",
                        color: COLORS.rowSubText,
                        fontSize: 14,
                      }}
                    >
                      Loading groups...
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td
                      colSpan={3}
                      style={{
                        padding: "40px 20px",
                        textAlign: "center",
                        color: COLORS.danger,
                        fontSize: 14,
                      }}
                    >
                      {error}
                    </td>
                  </tr>
                ) : filteredGroups.length > 0 ? (
                  filteredGroups.map((group) => (
                    <tr
                      key={group.id}
                      onClick={() =>
                        openRow(group.id)
                      }
                      style={{
                        background: "#FFFFFF",
                        borderTop: `1px solid ${COLORS.border}`,
                        cursor: "pointer",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background =
                          "#FAFBFC";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background =
                          "#FFFFFF";
                      }}
                    >
                      <td
                        style={{
                          padding: "16px",
                          fontSize: 14,
                          color: COLORS.rowText,
                          verticalAlign: "top",
                        }}
                      >
                        {group.name}
                      </td>

                      <td
                        style={{
                          padding: "16px",
                          fontSize: 14,
                          color: COLORS.rowSubText,
                          verticalAlign: "top",
                        }}
                      >
                        {group.description?.length >
                        90
                          ? `${group.description.slice(
                              0,
                              90
                            )}...`
                          : group.description}
                      </td>

                      <td
                        style={{
                          padding: "16px",
                          textAlign: "right",
                          verticalAlign: "top",
                        }}
                      >
                        <div
                          style={{
                            display: "inline-flex",
                            gap: 10,
                          }}
                        >
                          <button
                            title="Edit"
                            onClick={(e) =>
                              openEditForm(
                                group,
                                e
                              )
                            }
                            style={iconBtn}
                          >
                            <Pencil
                              size={15}
                              color="#4B5563"
                            />
                          </button>

                          <button
                            title="Delete"
                            onClick={(e) =>
                              handleDeleteGroup(
                                group.id,
                                e
                              )
                            }
                            style={iconBtn}
                          >
                            <Trash2
                              size={15}
                              color={
                                COLORS.danger
                              }
                            />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={3}
                      style={{
                        padding: "32px 20px",
                        textAlign: "center",
                        color: COLORS.rowSubText,
                        fontSize: 14,
                      }}
                    >
                      No groups found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ==================================================
          GROUP DETAIL
      ================================================== */}

      {view === "detail" && selectedGroup && (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 14,
              marginBottom: 20,
            }}
          >
            <span
              style={{
                color: COLORS.rowSubText,
                cursor: "pointer",
              }}
              onClick={backToList}
            >
              Group
            </span>

            <ChevronRight
              size={14}
              color={COLORS.rowSubText}
            />

            <span
              style={{
                color: COLORS.rowText,
                fontWeight: 500,
              }}
            >
              {selectedGroup.name}
            </span>
          </div>

          {/* GROUP INFORMATION */}

          <div
            style={{
              background: COLORS.headerBg,
              border: `1px solid ${COLORS.border}`,
              borderRadius: 10,
              padding: "20px 24px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 16,
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: 40,
                  flexWrap: "wrap",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 12,
                      color: COLORS.rowSubText,
                      fontWeight: 600,
                      textTransform:
                        "uppercase",
                      marginBottom: 6,
                    }}
                  >
                    Group Name
                  </div>

                  <div
                    style={{
                      fontSize: 15,
                      color: COLORS.rowText,
                      fontWeight: 600,
                    }}
                  >
                    {selectedGroup.name}
                  </div>
                </div>

                <div>
                  <div
                    style={{
                      fontSize: 12,
                      color: COLORS.rowSubText,
                      fontWeight: 600,
                      textTransform:
                        "uppercase",
                      marginBottom: 6,
                    }}
                  >
                    Group Key
                  </div>

                  <div
                    style={{
                      fontSize: 15,
                      color: COLORS.rowText,
                      fontWeight: 600,
                    }}
                  >
                    {selectedGroup.key
                      ? selectedGroup.enterKey || "—"
                      : "Off"}
                  </div>
                </div>

                <div>
                  <div
                    style={{
                      fontSize: 12,
                      color: COLORS.rowSubText,
                      fontWeight: 600,
                      textTransform:
                        "uppercase",
                      marginBottom: 6,
                    }}
                  >
                    Maximum Limit
                  </div>

                  <div
                    style={{
                      fontSize: 15,
                      color: COLORS.rowText,
                      fontWeight: 600,
                    }}
                  >
                    {selectedGroup.key && selectedGroup.maxLimit
                      ? `${(selectedGroup.members || []).length} / ${selectedGroup.maxLimit}`
                      : "No limit"}
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 10,
                }}
              >
                <button
                  title="Edit"
                  onClick={() =>
                    openEditForm(
                      selectedGroup
                    )
                  }
                  style={iconBtn}
                >
                  <Pencil
                    size={15}
                    color="#4B5563"
                  />
                </button>

                <button
                  title="Delete"
                  onClick={(e) =>
                    handleDeleteGroup(
                      selectedGroup.id,
                      e
                    )
                  }
                  style={iconBtn}
                >
                  <Trash2
                    size={15}
                    color={COLORS.danger}
                  />
                </button>
              </div>
            </div>

            <div
              style={{
                marginTop: 20,
                paddingTop: 20,
                borderTop: `1px solid ${COLORS.border}`,
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  color: COLORS.rowSubText,
                  fontWeight: 600,
                  textTransform:
                    "uppercase",
                  marginBottom: 8,
                }}
              >
                Description
              </div>

              <div
                style={{
                  fontSize: 14,
                  color: COLORS.rowText,
                  lineHeight: 1.7,
                }}
              >
                {selectedGroup.description}
              </div>
            </div>
          </div>

          {/* TABS */}

          <div
            style={{
              display: "flex",
              gap: 24,
              marginTop: 28,
              borderBottom: `1px solid ${COLORS.border}`,
            }}
          >
            {["People", "Course", "File"].map(
              (tab) => (
                <div
                  key={tab}
                  onClick={() => {
                    setDetailTab(tab);
                    if (!selectedGroup) return;
                    if (tab === "Course" && !selectedGroup.coursesLoaded) {
                      loadGroupCourses(selectedGroup.id);
                    } else if (tab === "File" && !selectedGroup.filesLoaded) {
                      loadGroupFiles(selectedGroup.id);
                    }
                  }}
                  style={{
                    fontSize: 15,
                    fontWeight: detailTab === tab ? 500 : 400,
                    color:
                      detailTab === tab
                        ? COLORS.rowText
                        : COLORS.rowSubText,
                    paddingBottom: 10,
                    borderBottom:
                      detailTab === tab
                        ? `2px solid ${COLORS.rowText}`
                        : "2px solid transparent",
                    cursor: "pointer",
                  }}
                >
                  {tab}
                </div>
              )
            )}
          </div>

          {/* ==================================================
              PEOPLE TAB
          ================================================== */}

          {detailTab === "People" && (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent:
                    "space-between",
                  alignItems: "center",
                  marginTop: 18,
                }}
              >
                <div
                  style={{
                    position: "relative",
                  }}
                >
                  <Search
                    size={15}
                    color="#9CA3AF"
                    style={{
                      position:
                        "absolute",
                      left: 10,
                      top: "50%",
                      transform:
                        "translateY(-50%)",
                    }}
                  />

                  <input
                    value={memberSearch}
                    onChange={(e) =>
                      setMemberSearch(
                        e.target.value
                      )
                    }
                    placeholder="Search"
                    style={{
                      ...fieldStyle,
                      width: 260,
                      padding:
                        "8px 12px 8px 32px",
                      fontSize: 14,
                    }}
                  />
                </div>

                <button
                  onClick={
                    openAddEmployees
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    ...primaryBtn,
                    padding: "9px 16px",
                  }}
                >
                  <Plus size={16} />
                  Add Employees
                </button>
              </div>

              <div
                style={{
                  marginTop: 16,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 8,
                  overflow: "hidden",
                }}
              >
                <table
                  style={{
                    width: "100%",
                    borderCollapse:
                      "collapse",
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        background:
                          COLORS.headerBg,
                      }}
                    >
                      <th
                        style={{
                          padding: "16px",
                          fontSize: 14,
                          fontWeight: 600,
                          textAlign: "left",
                          color:
                            COLORS.headerText,
                        }}
                      >
                        Employee ID
                      </th>

                      <th
                        style={{
                          padding: "16px",
                          fontSize: 14,
                          fontWeight: 600,
                          textAlign: "left",
                          color:
                            COLORS.headerText,
                        }}
                      >
                        Employee Name
                      </th>

                      <th
                        style={{
                          padding: "16px",
                          fontSize: 14,
                          fontWeight: 600,
                          textAlign: "left",
                          color:
                            COLORS.headerText,
                        }}
                      >
                        Role
                      </th>

                      <th
                        style={{
                          padding: "16px",
                          fontSize: 14,
                          fontWeight: 600,
                          textAlign: "left",
                          color:
                            COLORS.headerText,
                        }}
                      >
                        Courses Enrolled
                      </th>

                      <th
                        style={{
                          padding: "16px",
                          fontSize: 14,
                          fontWeight: 600,
                          textAlign: "right",
                          color: COLORS.headerText,
                        }}
                      >
                        Action
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {membersLoading ? (
                      <tr>
                        <td
                          colSpan={5}
                          style={{
                            padding: "28px 20px",
                            textAlign: "center",
                            color: COLORS.rowSubText,
                          }}
                        >
                          Loading employees...
                        </td>
                      </tr>
                    ) : filteredMembers.length >
                    0 ? (
                      filteredMembers.map(
                        (member) => (
                          <tr
                            key={member.id}
                            style={{
                              borderTop: `1px solid ${COLORS.border}`,
                            }}
                          >
                            <td
                              style={{
                                padding:
                                  "16px",
                                fontSize: 14,
                              }}
                            >
                              {member.empId}
                            </td>

                            <td
                              style={{
                                padding:
                                  "16px",
                                fontSize: 14,
                              }}
                            >
                              {member.name}
                            </td>

                            <td
                              style={{
                                padding:
                                  "16px",
                                fontSize: 14,
                                color:
                                  COLORS.rowSubText,
                              }}
                            >
                              {member.role}
                            </td>

                            <td
                              style={{
                                padding:
                                  "16px",
                                fontSize: 14,
                                color:
                                  COLORS.rowSubText,
                              }}
                            >
                              {member.courses}
                            </td>

                            <td
                              style={{
                                padding: "16px",
                                textAlign: "right",
                              }}
                            >
                              <button
                                title="Remove"
                                onClick={() =>
                                  handleRemoveMember(member.id)
                                }
                                style={iconBtn}
                              >
                                <Trash2 size={15} color={COLORS.danger} />
                              </button>
                            </td>
                          </tr>
                        )
                      )
                    ) : (
                      <tr>
                        <td
                          colSpan={5}
                          style={{
                            padding:
                              "28px 20px",
                            textAlign:
                              "center",
                            color: membersError
                              ? COLORS.danger
                              : COLORS.rowSubText,
                          }}
                        >
                          {membersError ||
                            "No employees in this group yet."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* ==================================================
              COURSE TAB
          ================================================== */}

          {detailTab === "Course" && (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent:
                    "space-between",
                  alignItems: "center",
                  marginTop: 18,
                }}
              >
                <div
                  style={{
                    position: "relative",
                  }}
                >
                  <Search
                    size={15}
                    color="#9CA3AF"
                    style={{
                      position:
                        "absolute",
                      left: 10,
                      top: "50%",
                      transform:
                        "translateY(-50%)",
                    }}
                  />

                  <input
                    value={courseSearch}
                    onChange={(e) =>
                      setCourseSearch(
                        e.target.value
                      )
                    }
                    placeholder="Search"
                    style={{
                      ...fieldStyle,
                      width: 260,
                      padding:
                        "8px 12px 8px 32px",
                      fontSize: 14,
                    }}
                  />
                </div>

                <button
                  onClick={
                    openCourseChoose
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    ...primaryBtn,
                    padding: "9px 16px",
                  }}
                >
                  <Plus size={16} />
                  Add Course
                </button>
              </div>

              <div
                style={{
                  marginTop: 16,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 8,
                  overflow: "hidden",
                }}
              >
                <table
                  style={{
                    width: "100%",
                    borderCollapse:
                      "collapse",
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        background:
                          COLORS.headerBg,
                      }}
                    >
                      <th
                        style={{
                          padding: "16px",
                          fontSize: 14,
                          fontWeight: 600,
                          textAlign: "left",
                          color:
                            COLORS.headerText,
                        }}
                      >
                        Code
                      </th>

                      <th
                        style={{
                          padding: "16px",
                          fontSize: 14,
                          fontWeight: 600,
                          textAlign: "left",
                          color:
                            COLORS.headerText,
                        }}
                      >
                        Course Name
                      </th>

                      <th
                        style={{
                          padding: "16px",
                          fontSize: 14,
                          fontWeight: 600,
                          textAlign: "left",
                          color:
                            COLORS.headerText,
                        }}
                      >
                        Category
                      </th>

                      <th
                        style={{
                          padding: "16px",
                          fontSize: 14,
                          fontWeight: 600,
                          textAlign: "left",
                          color:
                            COLORS.headerText,
                        }}
                      >
                        Price ($)
                      </th>

                      <th
                        style={{
                          padding: "16px",
                          fontSize: 14,
                          fontWeight: 600,
                          textAlign: "right",
                          color: COLORS.headerText,
                        }}
                      >
                        Action
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {coursesLoading ? (
                      <tr>
                        <td
                          colSpan={5}
                          style={{
                            padding: "28px 20px",
                            textAlign: "center",
                            color: COLORS.rowSubText,
                          }}
                        >
                          Loading courses...
                        </td>
                      </tr>
                    ) : filteredCourses.length >
                    0 ? (
                      filteredCourses.map(
                        (course) => (
                          <tr
                            key={course.id}
                            style={{
                              borderTop: `1px solid ${COLORS.border}`,
                            }}
                          >
                            <td
                              style={{
                                padding:
                                  "16px",
                              }}
                            >
                              {course.code}
                            </td>

                            <td
                              style={{
                                padding:
                                  "16px",
                              }}
                            >
                              {course.name}
                            </td>

                            <td
                              style={{
                                padding:
                                  "16px",
                                color:
                                  COLORS.rowSubText,
                              }}
                            >
                              {course.category}
                            </td>

                            <td
                              style={{
                                padding:
                                  "16px",
                                color:
                                  COLORS.rowSubText,
                              }}
                            >
                              {course.price}
                            </td>

                            <td
                              style={{
                                padding: "16px",
                                textAlign: "right",
                              }}
                            >
                              <button
                                title="Remove"
                                onClick={() =>
                                  handleRemoveCourse(course.id)
                                }
                                style={iconBtn}
                              >
                                <Trash2 size={15} color={COLORS.danger} />
                              </button>
                            </td>
                          </tr>
                        )
                      )
                    ) : (
                      <tr>
                        <td
                          colSpan={5}
                          style={{
                            padding:
                              "28px 20px",
                            textAlign:
                              "center",
                            color: coursesError
                              ? COLORS.danger
                              : COLORS.rowSubText,
                          }}
                        >
                          {coursesError ||
                            "No courses added yet."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* ==================================================
              FILE TAB
          ================================================== */}

          {detailTab === "File" && (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent:
                    "flex-end",
                  marginTop: 18,
                }}
              >
                <button
                  onClick={openAddFile}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    ...primaryBtn,
                    padding: "9px 16px",
                  }}
                >
                  <Plus size={16} />
                  Add File
                </button>
              </div>

              <div
                style={{
                  marginTop: 16,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 8,
                  overflow: "hidden",
                }}
              >
                <table
                  style={{
                    width: "100%",
                    borderCollapse:
                      "collapse",
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        background:
                          COLORS.headerBg,
                      }}
                    >
                      <th
                        style={{
                          padding: "16px",
                          fontSize: 14,
                          fontWeight: 600,
                          textAlign: "left",
                          color:
                            COLORS.headerText,
                        }}
                      >
                        File Name
                      </th>

                      <th
                        style={{
                          padding: "16px",
                          fontSize: 14,
                          fontWeight: 600,
                          textAlign: "right",
                          color:
                            COLORS.headerText,
                        }}
                      >
                        Action
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {filesLoading ? (
                      <tr>
                        <td
                          colSpan={2}
                          style={{
                            padding: "28px 20px",
                            textAlign: "center",
                            color: COLORS.rowSubText,
                          }}
                        >
                          Loading files...
                        </td>
                      </tr>
                    ) : selectedGroup.files
                      .length > 0 ? (
                      selectedGroup.files.map(
                        (file) => (
                          <tr
                            key={file.id}
                            style={{
                              borderTop: `1px solid ${COLORS.border}`,
                            }}
                          >
                            <td
                              style={{
                                padding:
                                  "16px",
                              }}
                            >
                              {file.name}
                            </td>

                            <td
                              style={{
                                padding:
                                  "16px",
                                textAlign:
                                  "right",
                              }}
                            >
                              <div
                                style={{
                                  display:
                                    "inline-flex",
                                  gap: 8,
                                }}
                              >
                                <button
                                  title="View"
                                  onClick={() =>
                                    setViewingFile(
                                      file
                                    )
                                  }
                                  style={
                                    iconBtn
                                  }
                                >
                                  <Eye
                                    size={15}
                                  />
                                </button>

                                <button
                                  title="Delete"
                                  onClick={() =>
                                    handleDeleteFile(
                                      file.id
                                    )
                                  }
                                  style={
                                    iconBtn
                                  }
                                >
                                  <Trash2
                                    size={15}
                                    color={
                                      COLORS.danger
                                    }
                                  />
                                </button>
                              </div>
                            </td>
                          </tr>
                        )
                      )
                    ) : (
                      <tr>
                        <td
                          colSpan={2}
                          style={{
                            padding:
                              "28px 20px",
                            textAlign:
                              "center",
                            color: filesListError
                              ? COLORS.danger
                              : COLORS.rowSubText,
                          }}
                        >
                          {filesListError ||
                            "No files uploaded yet."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}

      {/* ==================================================
          CREATE / EDIT GROUP MODAL
      ================================================== */}

      <Modal
        open={formOpen}
        onClose={() =>
          setFormOpen(false)
        }
      >
        <ModalHeader
          title={
            formMode === "edit"
              ? "Edit Group"
              : "Creating Group"
          }
          onClose={() =>
            setFormOpen(false)
          }
        />

        <div style={{ padding: 24 }}>
          <div style={{ marginBottom: 20 }}>
            <FormLabel>
              Group Name
            </FormLabel>

            <input
              value={form.name}
              onFocus={() =>
                setFocusField("name")
              }
              onBlur={() =>
                setFocusField(null)
              }
              onChange={(e) =>
                setForm({
                  ...form,
                  name: e.target.value,
                })
              }
              placeholder="Enter group name"
              style={inputStyle("name")}
            />
          </div>

          <div style={{ marginBottom: 20 }}>
            <FormLabel>
              Description
            </FormLabel>

            <textarea
              value={form.description}
              onFocus={() =>
                setFocusField(
                  "description"
                )
              }
              onBlur={() =>
                setFocusField(null)
              }
              onChange={(e) =>
                setForm({
                  ...form,
                  description:
                    e.target.value,
                })
              }
              placeholder="Enter description"
              rows={4}
              style={{
                ...inputStyle(
                  "description"
                ),
                resize: "vertical",
              }}
            />
          </div>

          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 15,
              color: COLORS.rowText,
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={form.groupKey}
              onChange={(e) =>
                setForm({
                  ...form,
                  groupKey:
                    e.target.checked,
                })
              }
              style={{
                width: 16,
                height: 16,
                accentColor:
                  COLORS.primary,
              }}
            />

            Group Key
          </label>

          {/* Ticking Group Key reveals the key itself and an optional cap
              on how many people the group can hold. */}
          {form.groupKey && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 16,
                marginTop: 16,
              }}
            >
              <div>
                <FormLabel>Enter Group Key</FormLabel>
                <input
                  value={form.enterGroupKey}
                  onFocus={() => setFocusField("enterGroupKey")}
                  onBlur={() => setFocusField(null)}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      enterGroupKey: e.target.value.replace(/\s/g, ""),
                    })
                  }
                  placeholder="e.g. DESIGN2026"
                  maxLength={20}
                  style={inputStyle("enterGroupKey")}
                />
              </div>

              <div>
                <FormLabel>Maximum Limit</FormLabel>
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={form.maxLimit}
                  onFocus={() => setFocusField("maxLimit")}
                  onBlur={() => setFocusField(null)}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      maxLimit: e.target.value,
                    })
                  }
                  placeholder="No limit"
                  style={inputStyle("maxLimit")}
                />
              </div>
            </div>
          )}

          {formError && (
            <div
              style={{
                marginTop: 14,
                fontSize: 13,
                color: COLORS.danger,
              }}
            >
              {formError}
            </div>
          )}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            padding: "16px 24px",
            borderTop: `1px solid ${COLORS.border}`,
          }}
        >
          <button
            onClick={() => {
              setFormOpen(false);
              setFormError("");
            }}
            style={ghostBtn}
          >
            Cancel
          </button>

          <button
            onClick={handleFormSubmit}
            disabled={formSubmitting}
            style={{
              ...primaryBtn,
              opacity: formSubmitting ? 0.6 : 1,
            }}
          >
            {formSubmitting
              ? "Saving..."
              : formMode === "edit"
              ? "Update"
              : "Create"}
          </button>
        </div>
      </Modal>

      {/* ==================================================
          CHOOSE EMPLOYEES
      ================================================== */}

      <Modal
        open={addStep === "choose"}
        onClose={closeAddFlow}
      >
        <ModalHeader
          title="Add employees to group"
          onClose={closeAddFlow}
        />

        <div
          style={{
            maxHeight: 360,
            overflowY: "auto",
            padding: "8px 24px",
          }}
        >
          {availableEmployeesLoading ? (
            <div
              style={{
                padding: "24px 0",
                textAlign: "center",
                color: COLORS.rowSubText,
                fontSize: 14,
              }}
            >
              Loading employees...
            </div>
          ) : availableEmployees.length === 0 ? (
            <div
              style={{
                padding: "24px 0",
                textAlign: "center",
                color: COLORS.rowSubText,
                fontSize: 14,
              }}
            >
              Everyone is already in this group.
            </div>
          ) : (
            <>
              {addSeatsLeft !== null && (
                <div
                  style={{
                    padding: "12px 0 4px",
                    fontSize: 13,
                    color: COLORS.rowSubText,
                  }}
                >
                  Maximum limit {selectedGroup.maxLimit} - you can add{" "}
                  {addSeatsLeft - selectedEmpIds.size} more
                  {addSeatsLeft - selectedEmpIds.size === 1 ? " person" : " people"}.
                </div>
              )}

              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "12px 0",
                  borderBottom: `1px solid ${COLORS.border}`,
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={
                    selectedEmpIds.size > 0 &&
                    selectedEmpIds.size ===
                      Math.min(
                        availableEmployees.length,
                        addSeatsLeft ?? availableEmployees.length
                      )
                  }
                  onChange={toggleAllEmps}
                />

                All
              </label>

              {availableEmployees.map((employee) => {
                // Limit reached: anyone not already picked is greyed out.
                const blocked =
                  addSeatsLeft !== null &&
                  !selectedEmpIds.has(employee.id) &&
                  selectedEmpIds.size >= addSeatsLeft;
                return (
                <label
                  key={employee.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent:
                      "space-between",
                    padding: "12px 0",
                    borderBottom: `1px solid ${COLORS.border}`,
                    fontSize: 15,
                    cursor: blocked ? "not-allowed" : "pointer",
                    opacity: blocked ? 0.45 : 1,
                  }}
                >
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                    }}
                  >
                    <input
                      type="checkbox"
                      disabled={blocked}
                      checked={selectedEmpIds.has(
                        employee.id
                      )}
                      onChange={() =>
                        toggleEmp(
                          employee.id
                        )
                      }
                    />

                    {employee.name}
                  </span>

                  <span
                    style={{
                      color:
                        COLORS.rowSubText,
                      fontSize: 14,
                    }}
                  >
                    {employee.empId}
                  </span>
                </label>
                );
              })}
            </>
          )}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            padding: "16px 24px",
            borderTop: `1px solid ${COLORS.border}`,
          }}
        >
          <button
            onClick={closeAddFlow}
            style={ghostBtn}
          >
            Cancel
          </button>

          <button
            onClick={
              handleEnrollFromList
            }
            disabled={
              selectedEmpIds.size === 0 || enrolling
            }
            style={{
              ...primaryBtn,
              opacity:
                selectedEmpIds.size ===
                  0 || enrolling
                  ? 0.5
                  : 1,
            }}
          >
            {enrolling ? "Enrolling..." : "Enroll"}
          </button>
        </div>
      </Modal>

      <SuccessModal
        open={!!createdGroupName}
        onClose={() => setCreatedGroupName(null)}
        title="Created"
        message={`"${createdGroupName}" group has been created successfully.`}
      />

      <SuccessModal
        open={addStep === "success"}
        onClose={finishAddSuccess}
        title="Enrolled"
        message={`${lastAddedCount} employee${
          lastAddedCount === 1
            ? ""
            : "s"
        } added to the group.`}
      />

      {/* ==================================================
          ADD COURSE
      ================================================== */}

      <Modal
        open={courseStep === "choose"}
        onClose={closeCourseFlow}
      >
        <ModalHeader
          title="Choose course to add"
          onClose={closeCourseFlow}
        />

        <div
          style={{
            maxHeight: 360,
            overflowY: "auto",
            padding: "8px 24px",
          }}
        >
          {availableCoursesLoading ? (
            <div
              style={{
                padding: "24px 0",
                textAlign: "center",
                color: COLORS.rowSubText,
                fontSize: 14,
              }}
            >
              Loading courses...
            </div>
          ) : availableCourses.length === 0 ? (
            <div
              style={{
                padding: "24px 0",
                textAlign: "center",
                color: COLORS.rowSubText,
                fontSize: 14,
              }}
            >
              Every course is already in this group.
            </div>
          ) : (
            <>
              <label
                style={{
                  display: "flex",
                  gap: 12,
                  padding: "12px 0",
                  borderBottom: `1px solid ${COLORS.border}`,
                }}
              >
                <input
                  type="checkbox"
                  checked={
                    selectedCourseIds.size ===
                    availableCourses.length
                  }
                  onChange={
                    toggleAllCourses
                  }
                />

                All
              </label>

              {availableCourses.map((course) => (
                <label
                  key={course.id}
                  style={{
                    display: "flex",
                    gap: 12,
                    padding: "12px 0",
                    borderBottom: `1px solid ${COLORS.border}`,
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedCourseIds.has(
                      course.id
                    )}
                    onChange={() =>
                      toggleCourse(
                        course.id
                      )
                    }
                  />

                  {course.name}
                </label>
              ))}
            </>
          )}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent:
              "flex-end",
            gap: 10,
            padding: "16px 24px",
            borderTop: `1px solid ${COLORS.border}`,
          }}
        >
          <button
            onClick={closeCourseFlow}
            style={ghostBtn}
          >
            Cancel
          </button>

          <button
            onClick={handleAddCourses}
            disabled={
              selectedCourseIds.size ===
                0 || addingCourses
            }
            style={{
              ...primaryBtn,
              opacity:
                selectedCourseIds.size ===
                  0 || addingCourses
                  ? 0.5
                  : 1,
            }}
          >
            {addingCourses ? "Adding..." : "Add"}
          </button>
        </div>
      </Modal>

      <SuccessModal
        open={courseStep === "success"}
        onClose={
          finishCourseSuccess
        }
        title="Course Added"
        message={`${lastAddedCourseCount} course${
          lastAddedCourseCount ===
          1
            ? ""
            : "s"
        } added to the group.`}
      />

      {/* ==================================================
          FILE MODAL
      ================================================== */}

      <Modal
        open={fileFormOpen}
        onClose={() =>
          setFileFormOpen(false)
        }
        maxWidth={460}
      >
        <ModalHeader
          title="Upload File"
          onClose={() =>
            setFileFormOpen(false)
          }
        />

        {(
          <div style={{ padding: 24 }}>
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setIsDraggingFile(true);
              }}
              onDragLeave={() =>
                setIsDraggingFile(false)
              }
              onDrop={handleFileDrop}
              style={{
                border: `1.5px dashed ${
                  isDraggingFile
                    ? COLORS.primary
                    : COLORS.border
                }`,
                borderRadius: 8,
                padding:
                  "32px 20px",
                textAlign: "center",
              }}
            >
              <Upload
                size={22}
                color={
                  COLORS.rowSubText
                }
              />

              <div
                style={{
                  marginTop: 12,
                  fontWeight: 600,
                }}
              >
                Drag files here to
                upload
              </div>

              <div
                style={{
                  marginTop: 6,
                  fontSize: 13,
                  color:
                    COLORS.rowSubText,
                }}
              >
                Alternatively, select
                file by{" "}
                <span
                  onClick={() =>
                    fileInputRef.current?.click()
                  }
                  style={{
                    color:
                      COLORS.primary,
                    fontWeight: 600,
                    textDecoration:
                      "underline",
                    cursor:
                      "pointer",
                  }}
                >
                  clicking here
                </span>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={
                  handleFilePicked
                }
                style={{
                  position: "absolute",
                  width: 1,
                  height: 1,
                  padding: 0,
                  margin: -1,
                  overflow: "hidden",
                  border: 0,
                  opacity: 0,
                }}
              />
            </div>

            {stagedFiles.length >
              0 && (
              <div
                style={{
                  marginTop: 16,
                }}
              >
                {stagedFiles.map(
                  (file, index) => (
                    <div
                      key={`${file.name}-${index}`}
                      style={{
                        display:
                          "flex",
                        justifyContent:
                          "space-between",
                        padding:
                          "10px 0",
                        borderBottom: `1px solid ${COLORS.border}`,
                      }}
                    >
                      {file.name}

                      <button
                        onClick={() =>
                          removeStagedFile(
                            index
                          )
                        }
                        style={{
                          border: "none",
                          background:
                            "transparent",
                          cursor:
                            "pointer",
                        }}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  )
                )}
              </div>
            )}

            {fileError && (
              <div
                style={{
                  marginTop: 12,
                  fontSize: 13,
                  color: COLORS.danger,
                }}
              >
                {fileError}
              </div>
            )}
          </div>
        )}

        <div
          style={{
            display: "flex",
            justifyContent:
              "flex-end",
            gap: 10,
            padding: "16px 24px",
            borderTop: `1px solid ${COLORS.border}`,
          }}
        >
          <button
            onClick={() =>
              setFileFormOpen(false)
            }
            style={ghostBtn}
          >
            Cancel
          </button>

          <button
            onClick={
              handleFileFormSubmit
            }
            disabled={fileUploading}
            style={{
              ...primaryBtn,
              opacity:
                stagedFiles.length === 0 || fileUploading ? 0.5 : 1,
            }}
          >
            {fileUploading ? "Uploading..." : "Save"}
          </button>
        </div>
      </Modal>

      {/* ==================================================
          VIEW FILE
      ================================================== */}

      <Modal
        open={Boolean(viewingFile)}
        onClose={() =>
          setViewingFile(null)
        }
        maxWidth={400}
      >
        <ModalHeader
          title="File Details"
          onClose={() =>
            setViewingFile(null)
          }
        />

        <div style={{ padding: 24 }}>
          <div
            style={{
              fontSize: 12,
              color: COLORS.rowSubText,
              fontWeight: 600,
              textTransform:
                "uppercase",
              marginBottom: 8,
            }}
          >
            File Name
          </div>

          <div
            style={{
              fontSize: 15,
              fontWeight: 600,
              color: COLORS.rowText,
            }}
          >
            {viewingFile?.name}
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            padding: "16px 24px",
            borderTop: `1px solid ${COLORS.border}`,
          }}
        >
          {viewingFile?.url ? (
            <a
              href={viewingFile.url}
              target="_blank"
              rel="noreferrer"
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: COLORS.primary,
              }}
            >
              Open file
            </a>
          ) : (
            <span />
          )}

          <button
            onClick={() =>
              setViewingFile(null)
            }
            style={ghostBtn}
          >
            Close
          </button>
        </div>
      </Modal>
    </div>
  );
}